import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getVaultNewRecords, markVaultRecordsSeen } from './api';
import type { VaultNewRecordsValue } from './context';
import type { VaultNewRecordsByTable, VaultSeenRecords } from './types';

/** The websocket service re-dispatches every server message on `document` under this event name. */
const WEBSOCKET_MESSAGE_EVENT = 'private-ws-message';

const isParseCompletedMessage = (message: unknown): boolean => {
  if (typeof message !== 'object' || message === null) return false;
  const { type, data } = message as { type?: unknown; data?: { status?: unknown } };
  return type === 'ai-job.user' && data?.status === 'COMPLETED';
};

/** `byTable` without the given ids; a table left with none is dropped. */
const withoutRecords = (
  byTable: VaultNewRecordsByTable,
  remove: VaultSeenRecords
): VaultNewRecordsByTable => {
  const next: VaultNewRecordsByTable = {};
  for (const [name, ids] of Object.entries(byTable)) {
    const removed = new Set(remove[name] ?? []);
    const kept = removed.size === 0 ? ids : ids.filter((id) => !removed.has(id));
    if (kept.length > 0) next[name] = kept;
  }
  return next;
};

/**
 * [MCV-1812] Loads which vault records a document parse created and the provider has not seen yet.
 *
 * Reloads when a parse job completes (the API writes the rows before it sends that update), so a
 * document uploaded while the vault is open shows its New rows without a refresh.
 *
 * `readOnly` is for a delegated employer looking at the provider's vault: the marks show, but
 * their visit must not clear them for the provider.
 */
export const useVaultNewRecords = (
  providerId: number | undefined,
  { readOnly = false }: { readOnly?: boolean } = {}
): VaultNewRecordsValue => {
  const [byTable, setByTable] = useState<VaultNewRecordsByTable>({});
  // A load that finishes after the provider changed must not overwrite the new provider's map.
  const activeProviderRef = useRef(providerId);
  activeProviderRef.current = providerId;

  // The ids marked seen while a load is in flight. That load's snapshot predates the mark, so
  // exactly those ids are dropped from it before it is applied (otherwise a reload started by a
  // parse could bring back the New marks of rows the user just saw). A mark the
  // snapshot has that the user never saw stays.
  const seenDuringLoadsRef = useRef(new Set<VaultSeenRecords>());
  // Only the most recently started load may apply its answer; an older one that finishes later
  // would otherwise replace newer New marks with its earlier snapshot.
  const latestLoadRef = useRef(0);
  const load = useCallback(async () => {
    if (providerId === undefined) return;
    const loadId = ++latestLoadRef.current;
    const seenDuringThisLoad: VaultSeenRecords = {};
    seenDuringLoadsRef.current.add(seenDuringThisLoad);
    try {
      const response = await getVaultNewRecords(providerId);
      if (activeProviderRef.current !== providerId || latestLoadRef.current !== loadId) return;
      setByTable(withoutRecords(response?.byTable ?? {}, seenDuringThisLoad));
    } catch (error) {
      // Badges are a convenience; a failed read just means none are shown.
      console.warn('[vaultNewRecords] could not load new records', error);
    } finally {
      seenDuringLoadsRef.current.delete(seenDuringThisLoad);
    }
  }, [providerId]);

  useEffect(() => {
    setByTable({});
    void load();
  }, [load]);

  useEffect(() => {
    const onMessage = (event: Event) => {
      if (isParseCompletedMessage((event as CustomEvent).detail)) void load();
    };
    document.addEventListener(WEBSOCKET_MESSAGE_EVENT, onMessage);
    return () => document.removeEventListener(WEBSOCKET_MESSAGE_EVENT, onMessage);
  }, [load]);

  const markSeen = useCallback(
    (records: VaultSeenRecords) => {
      if (providerId === undefined || readOnly) return;
      if (Object.values(records).every((ids) => ids.length === 0)) return;
      for (const pending of seenDuringLoadsRef.current) {
        for (const [name, ids] of Object.entries(records)) {
          pending[name] = [...(pending[name] ?? []), ...ids];
        }
      }
      setByTable((current) => withoutRecords(current, records));
      markVaultRecordsSeen(providerId, records).catch((error) => {
        console.warn('[vaultNewRecords] could not mark records seen', error);
      });
    },
    [providerId, readOnly]
  );

  return useMemo(() => ({ byTable, markSeen }), [byTable, markSeen]);
};
